/**
 * PriorityCell Component
 *
 * Renders a priority column value with fixed color-coded levels.
 * Priority levels are fixed (Critical, High, Medium, Low, None).
 */

import { useState, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import type { PriorityCellProps } from './types';
import { PRIORITY_LEVELS } from '@wmp/shared/constants';

export function PriorityCell({
  value,
  onChange,
  readonly = false,
}: PriorityCellProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Find the current priority level
  const currentPriority = value
    ? PRIORITY_LEVELS.find(p => p.id === value.priority)
    : null;

  const handleSelect = (priorityId: string) => {
    if (readonly || !onChange) return;

    if (priorityId === 'none') {
      onChange({ priority: 'none' });
    } else {
      const selected = PRIORITY_LEVELS.find(p => p.id === priorityId);
      if (selected) {
        onChange({ priority: selected.id as 'critical' | 'high' | 'medium' | 'low' | 'none' });
      }
    }
    setIsOpen(false);
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {currentPriority && currentPriority.id !== 'none' ? (
          <span
            className="status-label"
            style={{ backgroundColor: currentPriority.color }}
          >
            {currentPriority.label}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <Listbox value={value?.priority ?? 'none'} onChange={handleSelect}>
      <div className="relative w-full">
        <Listbox.Button
          className="w-full h-full flex items-center cursor-pointer min-h-[28px]"
          onClick={() => setIsOpen(true)}
        >
          {currentPriority && currentPriority.id !== 'none' ? (
            <span
              className="status-label"
              style={{ backgroundColor: currentPriority.color }}
            >
              {currentPriority.label}
            </span>
          ) : (
            <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
          )}
        </Listbox.Button>

        <Transition
          show={isOpen}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setIsOpen(false)}
        >
          <Listbox.Options
            static
            className="absolute z-50 mt-1 w-36 bg-white rounded-lg shadow-dropdown border border-surface-border py-1 max-h-60 overflow-auto focus:outline-none"
          >
            {PRIORITY_LEVELS.map((priority) => (
              <Listbox.Option
                key={priority.id}
                value={priority.id}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 px-3 ${
                    active ? 'bg-surface-hover' : ''
                  }`
                }
              >
                {({ selected }) => (
                  <div className="flex items-center gap-2">
                    {priority.id !== 'none' ? (
                      <span
                        className="status-label text-xs"
                        style={{ backgroundColor: priority.color }}
                      >
                        {priority.label}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">None</span>
                    )}
                    {selected && <CheckIcon className="h-4 w-4 ml-auto text-primary-500" />}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
